/**
 * Imperial display for the Variant B WL questionnaire (UK). Answers are always stored in cm / kg
 * (BMI, blockers and the doctor view stay metric); only what the customer sees switches to
 * ft/in and st/lb. The BMI screen records the unit the customer picked so the later screens
 * (weight projection, health summary) show weights in the same unit.
 */
import { cmToImperial } from "../components/ImperialInput/imperialUtils";
import { formatStLb } from "../../../utils/weightUnits";
import { formatDecimal } from "../../../utils/decimalInput";

// Shared with the Care+ My Account weight display, so a weight reads the same everywhere.
export { formatStLb };

export type UnitSystem = "metric" | "imperial";
type UnitField = "weight" | "height";

const STORAGE_KEY = "wl_questionnaire_units";

const readPrefs = (): Partial<Record<UnitField, UnitSystem>> => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}") || {};
  } catch {
    return {};
  }
};

export const getUnitPref = (field: UnitField): UnitSystem | undefined => readPrefs()[field];

export const saveUnitPref = (field: UnitField, unit: UnitSystem): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readPrefs(), [field]: unit }));
  } catch {
    // storage unavailable — later screens fall back to metric
  }
};

export const isImperialWeight = (): boolean => getUnitPref("weight") === "imperial";
export const isImperialHeight = (): boolean => getUnitPref("height") === "imperial";

export const formatFtIn = (cm: number): string => {
  const { ft, inches } = cmToImperial(cm);
  return `${ft} ft ${inches} in`;
};

/** Weight in the customer's chosen unit, unit included ("11 st 3 lb" / "84,5 kg"). */
export const formatWeight = (kg: number, language?: string): string =>
  isImperialWeight() ? formatStLb(kg) : `${formatDecimal(kg, language)} kg`;
